/**
 * useReportReaderMode — the report reading surface's light/dark color mode.
 *
 * The report CONTENT was historically force-locked light (white page + dark
 * ink) regardless of the app theme — see `reportRoleColors.ts` (`DOC_BG`) and
 * the `[data-theme-scope='light']` island in `index.css`. This module lets the
 * ON-SCREEN reader instead follow the app's EXISTING light/dark toggle
 * (`src/lib/theme.ts` — the app is dark-by-default; a `light` class on
 * `<html>` = light mode), while every EXPORT surface (PDF / print / DOCX /
 * paged deck) stays pinned light.
 *
 * Two integration points:
 *   1. `useAppResolvedTheme()` — reactively reads the app theme class, mirroring
 *      the observer pattern in `useAuroraMapId.ts`. SiteRagReportView combines
 *      it with `audienceMode` (export ⇒ always light) to derive the reader mode.
 *   2. `ReportReaderModeContext` — provided at the report root so nested
 *      components that call `useEffectiveReportTheme()` directly also resolve
 *      the same mode (that hook stamps the resolved mode onto the theme it
 *      returns via `attachReaderMode`, so `roleColor(theme, role)` is
 *      mode-aware with zero call-site churn).
 *
 * The mode NEVER reaches export: `audienceMode === 'export'` forces `'light'`
 * at the SiteRagReportView seam, and the paged deck is independently light-
 * locked by the `.pagedjs_pages` rule in `index.css`. So the light + export
 * output — which every attestation baseline renders — is byte-identical.
 */
import * as React from 'react';

export type ReportReaderMode = 'light' | 'dark';

/** Read the app's resolved theme from the `<html>` class. The app is
 *  dark-by-default; a `light` class opts into light (see `src/lib/theme.ts`
 *  + `THEME_BOOTSTRAP_SCRIPT`, applied before React mounts so there is no
 *  first-paint flash). SSR / no-DOM ⇒ `'light'` (keeps non-browser renders
 *  and the light baseline stable). */
export function readAppResolvedTheme(): ReportReaderMode {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.classList.contains('light') ? 'light' : 'dark';
}

/**
 * Reactively resolve the app theme, re-reading whenever the `<html>` class
 * changes (the app toggle adds/removes `light`). Mirrors the MutationObserver
 * approach in `useAuroraMapId.ts` so the report reader tracks the toggle live.
 */
export function useAppResolvedTheme(): ReportReaderMode {
  const [mode, setMode] = React.useState<ReportReaderMode>(readAppResolvedTheme);
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const sync = () => setMode(readAppResolvedTheme());
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return mode;
}

/**
 * Report reader mode as seen by the report subtree. Provided at the report
 * root (SiteRagReportView) with the export-guarded value; default `'light'`
 * so any consumer OUTSIDE a report (or in an export host that never provides
 * it) resolves light — byte-identical to today.
 */
export const ReportReaderModeContext = React.createContext<ReportReaderMode>('light');

/** Read the report reader mode from context (default `'light'`). */
export function useReportReaderModeContext(): ReportReaderMode {
  return React.useContext(ReportReaderModeContext);
}
